import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";
import { forwardRef } from "react";

export function cx(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(" ");
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = "secondary", size = "md", children, ...props },
  ref,
) {
  return <button ref={ref} className={cx("button", `button--${variant}`, `button--${size}`, className)} {...props}>{children}</button>;
});

interface FieldProps {
  label: string;
  htmlFor: string;
  hint?: string | undefined;
  optional?: boolean;
  children: ReactNode;
}
export function Field({ label, htmlFor, hint, optional, children }: FieldProps) {
  return (
    <div className="field">
      <div className="field__heading">
        <label htmlFor={htmlFor}>{label}</label>
        {optional ? <span>可选</span> : null}
      </div>
      {children}
      {hint ? <p className="field__hint">{hint}</p> : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...props }, ref) {
  return <input ref={ref} className={cx("input", className)} {...props} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...props }, ref) {
  return <textarea ref={ref} className={cx("input", "textarea", className)} {...props} />;
});

export function Tag({
  children,
  tone = "neutral",
  variant = "badge",
}: {
  children: ReactNode;
  tone?: "neutral" | "accent" | "success";
  variant?: "badge" | "metadata";
}) {
  return <span className={cx("tag", `tag--${tone}`, `tag--${variant}`)}>{children}</span>;
}

export function PageHeader({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <header className="page-heading">
      <div>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {action ? <div className="page-heading__action">{action}</div> : null}
    </header>
  );
}

export function EmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <div className="empty-state__mark" aria-hidden="true">Aa</div>
      <h2>{title}</h2>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function LoadingLine() {
  return (
    <div className="loading-state" role="status" aria-label="加载中">
      <span className="loading-line" aria-hidden="true" />
    </div>
  );
}
